import { useCallback, useEffect, useState } from 'react';

/**
 * User-adjustable UI scale (the sidebar +/− control).
 *
 * Implemented by setting the root font-size. The whole app is built in `rem`
 * units, so scaling the root scales every text size, padding, and control
 * proportionally — the layout stays intact, it just grows or shrinks to fill
 * the screen to the user's taste. Persisted like the theme.
 */
const STORAGE_KEY = 'ecotone-ui-scale';
const BASE_PX = 16; // browser default root size

// Bounds keep the UI usable — below ~0.8 controls get too small to hit, above
// ~1.4 the sidebar text starts to wrap.
export const MIN_SCALE = 0.8;
export const MAX_SCALE = 1.4;
const STEP = 0.1;

function clamp(v: number) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(v * 100) / 100));
}

function stored(): number {
  const v = Number(localStorage.getItem(STORAGE_KEY));
  return Number.isFinite(v) && v >= MIN_SCALE && v <= MAX_SCALE ? v : 1;
}

function apply(scale: number) {
  document.documentElement.style.fontSize = `${BASE_PX * scale}px`;
}

/** Set the root size before React mounts so there's no resize flash on load. */
export function applyScaleEarly() {
  apply(stored());
}

export function useUiScale() {
  const [scale, setScale] = useState<number>(stored);

  useEffect(() => {
    apply(scale);
    localStorage.setItem(STORAGE_KEY, String(scale));
  }, [scale]);

  const increase = useCallback(() => setScale((s) => clamp(s + STEP)), []);
  const decrease = useCallback(() => setScale((s) => clamp(s - STEP)), []);
  const reset = useCallback(() => setScale(1), []);

  return {
    scale,
    increase,
    decrease,
    reset,
    canIncrease: scale < MAX_SCALE,
    canDecrease: scale > MIN_SCALE,
    /** Percentage for display, e.g. "110%". */
    percent: Math.round(scale * 100),
  };
}
